/* Tradeline home + content sections: hero slider, countdown, tabs, carousel arrows,
 * video facade, wishlist page and copy buttons. Each feature is a small custom element
 * and only runs where its markup exists. Safe to include more than once.
 */
(() => {
  if (window.TradelineSections) return;
  window.TradelineSections = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ---------- Hero slider ---------- */
  class TlSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-to]'));
      this.index = 0;
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      this.playing = this.delay > 0 && !reduceMotion() && this.slides.length > 1;
      this.pauseBtn = this.querySelector('[data-slider-pause]');
      this.show(0, false);

      this.addEventListener('click', (e) => {
        const to = e.target.closest('[data-slide-to]');
        if (to) { this.show(Number(to.dataset.slideTo)); this.stop(); return; }
        if (e.target.closest('[data-slider-prev]')) { this.show(this.index - 1); this.stop(); return; }
        if (e.target.closest('[data-slider-next]')) { this.show(this.index + 1); this.stop(); return; }
        if (e.target.closest('[data-slider-pause]')) { if (this.playing) this.stop(); else { this.playing = true; this.engaged = true; this.start(); } this.syncPause(); }
      });
      this.addEventListener('keydown', (e) => {
        if (e.target.closest('input, textarea, select')) return;
        if (e.key === 'ArrowLeft') { this.show(this.index - 1); this.stop(); }
        if (e.key === 'ArrowRight') { this.show(this.index + 1); this.stop(); }
      });
      this.addEventListener('mouseenter', () => this.hold(true));
      this.addEventListener('mouseleave', () => this.hold(false));
      this.addEventListener('focusin', () => this.hold(true));
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold(false); });

      let x0 = null;
      this.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') x0 = e.clientX; });
      this.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) { this.show(this.index + (dx < 0 ? 1 : -1)); this.stop(); }
      });

      this.onBlock = (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target));
        if (i < 0) return;
        if (e.type === 'shopify:block:select') { this.stop(); this.show(i, false); }
      };
      document.addEventListener('shopify:block:select', this.onBlock);
      this.syncPause();
      this.armAutoplay();
    }

    // Autoplay waits for the shopper's first interaction with the page and only runs
    // while the slider is on screen and the tab is visible: the first slide stays put
    // while the page loads (faster visual load, no motion before the shopper engages).
    armAutoplay() {
      if (!this.playing) return;
      this.visible = true;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.resume(); });
        this.io.observe(this);
      }
      this.onVisibility = () => this.resume();
      document.addEventListener('visibilitychange', this.onVisibility);
      const events = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
      const go = () => {
        events.forEach((t) => window.removeEventListener(t, go, { capture: true }));
        this.engaged = true;
        this.resume();
      };
      this.disarm = () => events.forEach((t) => window.removeEventListener(t, go, { capture: true }));
      if (window.Shopify && Shopify.designMode) { go(); return; }
      events.forEach((t) => window.addEventListener(t, go, { capture: true, passive: true }));
    }

    resume() { if (this.visible !== false && !document.hidden) this.start(); else clearInterval(this.timer); }

    disconnectedCallback() {
      clearInterval(this.timer);
      document.removeEventListener('shopify:block:select', this.onBlock);
      if (this.io) this.io.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.disarm) this.disarm();
    }

    show(i, focus = false) {
      const n = this.slides.length;
      if (!n) return;
      this.index = ((i % n) + n) % n;
      this.slides.forEach((s, k) => {
        const on = k === this.index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', String(!on));
        if ('inert' in s) s.inert = !on;
      });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', String(k === this.index)));
      if (focus) this.slides[this.index].focus();
    }

    start() {
      clearInterval(this.timer);
      if (this.playing && this.engaged && !this.held && this.visible !== false && !document.hidden) {
        this.timer = setInterval(() => this.show(this.index + 1), this.delay);
      }
    }

    stop() { this.playing = false; clearInterval(this.timer); this.syncPause(); }

    hold(on) { this.held = on; if (on) clearInterval(this.timer); else this.start(); }

    syncPause() {
      if (!this.pauseBtn) return;
      this.pauseBtn.hidden = !(this.delay > 0 && this.slides.length > 1);
      this.pauseBtn.setAttribute('aria-pressed', String(!this.playing));
      this.pauseBtn.setAttribute('aria-label', this.playing ? this.pauseBtn.dataset.labelPause : this.pauseBtn.dataset.labelPlay);
      this.pauseBtn.classList.toggle('is-paused', !this.playing);
    }
  }
  define('tl-slider', TlSlider);

  /* ---------- Countdown ---------- */
  class TlCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end || 0) * 1000;
      this.units = {};
      this.querySelectorAll('[data-unit]').forEach((el) => { this.units[el.dataset.unit] = el; });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    tick() {
      const left = Math.max(0, this.end - Date.now());
      const s = Math.floor(left / 1000);
      const parts = { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
      Object.entries(parts).forEach(([k, v]) => { if (this.units[k]) this.units[k].textContent = String(v).padStart(2, '0'); });
      if (left > 0) return;
      clearInterval(this.timer);
      const root = this.closest('[data-countdown-root]');
      if (this.dataset.ended === 'hide' && root && !(window.Shopify && Shopify.designMode)) { root.hidden = true; return; }
      const timer = this.querySelector('[data-countdown-timer]');
      const ended = this.querySelector('[data-countdown-ended]');
      if (timer) timer.hidden = true;
      if (ended) ended.hidden = false;
    }
  }
  define('tl-countdown', TlCountdown);

  /* ---------- Tabs ---------- */
  class TlTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const map = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: this.tabs.length - 1 };
          if (!(e.key in map)) return;
          e.preventDefault();
          const next = (map[e.key] + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    select(i) {
      this.tabs.forEach((tab, k) => {
        const on = k === i;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }
  define('tl-tabs', TlTabs);

  /* ---------- Scroll carousel arrows ---------- */
  class TlCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track) return;
      // In right-to-left layouts scrollLeft runs from 0 down to negative values and "next" moves left.
      const rtl = () => (getComputedStyle(this).direction === 'rtl' ? -1 : 1);
      const step = (dir) => this.track.scrollBy({ left: dir * rtl() * this.track.clientWidth * 0.9, behavior: reduceMotion() ? 'auto' : 'smooth' });
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        const x = Math.abs(this.track.scrollLeft);
        if (this.prev) this.prev.disabled = x <= 2;
        if (this.next) this.next.disabled = x >= max;
        this.classList.toggle('is-static', max <= 0);
      };
      this.track.addEventListener('scroll', () => window.requestAnimationFrame(this.update), { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }

    disconnectedCallback() { if (this.update) window.removeEventListener('resize', this.update); }
  }
  define('tl-carousel', TlCarousel);

  /* ---------- Video facade ---------- */
  class TlVideo extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-video-play]');
      if (btn) btn.addEventListener('click', () => this.load(), { once: true });
    }

    load() {
      const frame = this.querySelector('[data-video-frame]');
      if (!frame) return;
      if (this.dataset.src) {
        const iframe = document.createElement('iframe');
        iframe.src = this.dataset.src;
        iframe.title = this.dataset.title || '';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        frame.replaceChildren(iframe);
      } else {
        const tpl = this.querySelector('template');
        if (!tpl) return;
        frame.replaceChildren(tpl.content.cloneNode(true));
        const video = frame.querySelector('video');
        if (video) { video.play().catch(() => {}); }
      }
      this.classList.add('is-loaded');
      const focusTarget = frame.querySelector('iframe, video');
      if (focusTarget) focusTarget.focus();
    }
  }
  define('tl-video', TlVideo);

  /* ---------- Wishlist page ---------- */
  class TlWishlist extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.status = (this.closest('.tl-section') || document).querySelector('[data-wishlist-status]');
      this.onChange = () => this.prune();
      document.addEventListener('tl:wishlist:changed', this.onChange);
      this.render();
    }

    disconnectedCallback() { document.removeEventListener('tl:wishlist:changed', this.onChange); }

    handles() { const T = window.Tradeline; return T && T.wishlist ? T.wishlist.items() : []; }

    url(handle) {
      const root = ((window.Tradeline && Tradeline.routes && Tradeline.routes.root) || '/').replace(/\/$/, '');
      return `${root}/products/${encodeURIComponent(handle)}?view=card`;
    }

    async render() {
      const handles = this.handles();
      this.setEmpty(handles.length === 0);
      if (!handles.length) return;
      this.grid.setAttribute('aria-busy', 'true');
      const results = await Promise.all(handles.map((h) => fetch(this.url(h)).then((r) => (r.ok ? r.text() : '')).catch(() => '')));
      const frag = document.createDocumentFragment();
      results.forEach((html, i) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('.tl-card');
        if (!card) return;
        const li = document.createElement('li');
        li.className = 'tl-product-grid__item';
        li.dataset.handle = handles[i];
        li.appendChild(document.importNode(card, true));
        frag.appendChild(li);
      });
      this.grid.replaceChildren(frag);
      this.grid.removeAttribute('aria-busy');
      this.sync();
      this.setEmpty(this.grid.children.length === 0);
    }

    prune() {
      const keep = this.handles();
      Array.from(this.grid.children).forEach((li) => { if (!keep.includes(li.dataset.handle)) li.remove(); });
      this.setEmpty(this.grid.children.length === 0);
    }

    sync() {
      const T = window.Tradeline || {};
      this.grid.querySelectorAll('[data-wishlist]').forEach((b) => b.setAttribute('aria-pressed', String(!!(T.wishlist && T.wishlist.has(b.dataset.wishlist)))));
      this.grid.querySelectorAll('[data-compare]').forEach((b) => b.setAttribute('aria-pressed', String(!!(T.compare && T.compare.has(b.dataset.compare)))));
    }

    setEmpty(isEmpty) {
      if (this.empty) this.empty.hidden = !isEmpty;
      this.grid.hidden = isEmpty;
      if (this.status) {
        const n = this.grid.children.length;
        this.status.textContent = isEmpty ? '' : (this.status.dataset.template || '').replace('[count]', n);
      }
    }
  }
  define('tl-wishlist', TlWishlist);

  /* ---------- Copy to clipboard ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(btn.dataset.copy);
      const msg = btn.dataset.copied || '';
      if (window.Tradeline && Tradeline.toast && msg) Tradeline.toast(msg);
      btn.classList.add('is-copied');
      setTimeout(() => btn.classList.remove('is-copied'), 2000);
    } catch (err) { /* clipboard unavailable */ }
  });

  /* ---------- Native share (falls back to the plain share links) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-native-share]');
    if (!btn || !navigator.share) return;
    e.preventDefault();
    navigator.share({ title: btn.dataset.title, url: btn.dataset.url }).catch(() => {});
  });
  const showShare = () => document.querySelectorAll('[data-native-share]').forEach((b) => { if (navigator.share) b.hidden = false; });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', showShare); else showShare();
})();
